import { useCallback, useEffect, useMemo } from "react";

import { LabelWrapper, Select, Text, useMemoizedKeyMap } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    isWhirlpoolMetadata,
    useActiveWallet,
    useTokenListMetadataByMint,
    useUserWhirlpoolPositions
} from "@bridgesplit/abf-react";
import { WhirlpoolPositionExpanded } from "@bridgesplit/abf-sdk";
import { DispatchType } from "@bridgesplit/react";

import { WhirlpoolPositionSummarized } from "./whirlpool";

// Define the generic interface for forms that support LP positions
export interface FormWithLpPosition {
    orcaPosition?: WhirlpoolPositionExpanded;
    collateralAmount?: number;
    refetchOutAmount?: boolean;
    labelColor?: "body" | "caption";
}

// Generic LP Position Select component
export function GenericLpPositionSelect<T extends FormWithLpPosition>({
    collateralMint,
    form,
    setForm,
    positions,
    onPositionChange,
    labelColor = "body"
}: {
    collateralMint: string;
    form: T;
    setForm: DispatchType<T>;
    positions?: WhirlpoolPositionExpanded[];
    onPositionChange?: (position: WhirlpoolPositionExpanded) => Partial<T>;
    labelColor?: "body" | "caption";
}) {
    const metadata = useTokenListMetadataByMint(collateralMint);
    const { user } = useActiveWallet();

    const isOrcaPosition = isWhirlpoolMetadata(metadata);
    const { data: wp } = useUserWhirlpoolPositions({ skip: !isOrcaPosition });

    const availablePositions = useMemo(() => {
        // Use provided positions if available, otherwise fetch them
        if (positions) return positions;

        return user
            ? wp?.filter((w) => w.whirlpoolAddress === collateralMint).sort((a, b) => b.totalPrice - a.totalPrice)
            : [];
    }, [collateralMint, user, wp, positions]);

    const mintToPosition = useMemoizedKeyMap(availablePositions, (p) => p.position.positionMint);

    const setPosition = useCallback(
        (orcaPosition: WhirlpoolPositionExpanded) => {
            const updates = onPositionChange
                ? onPositionChange(orcaPosition)
                : {
                      orcaPosition,
                      collateralAmount: orcaPosition.position.totalLiquidity,
                      refetchOutAmount: true
                  };

            setForm((prev) => ({
                ...prev,
                ...updates
            }));
        },
        [setForm, onPositionChange]
    );

    // initially sets the position to the defined amount
    useEffect(() => {
        if (!isOrcaPosition || !availablePositions?.length || form.orcaPosition?.whirlpoolAddress === collateralMint)
            return;
        setPosition(availablePositions[0]);
    }, [collateralMint, form.orcaPosition, isOrcaPosition, availablePositions, setPosition]);

    if (!isOrcaPosition) return null;

    if (availablePositions?.length === 0) {
        return (
            <LabelWrapper color={labelColor} label="Position">
                <Text color="disabled" variant="body2">
                    You don't have any {BsMetaUtil.getName(metadata)} positions
                </Text>
            </LabelWrapper>
        );
    }

    return (
        <LabelWrapper color={labelColor} label="Position">
            <Select
                renderValue={() => <WhirlpoolPositionSummarized position={form.orcaPosition} />}
                value={availablePositions ? form.orcaPosition?.position.positionMint : ""}
                loading={!availablePositions}
                setValue={(positionMint) => {
                    const orcaPosition = mintToPosition?.get(positionMint);
                    if (!orcaPosition) return;
                    setPosition(orcaPosition);
                }}
                options={(availablePositions ?? []).map((position) => {
                    return {
                        label: <WhirlpoolPositionSummarized spaceBetween position={position} />,
                        value: position.position.positionMint
                    };
                })}
            />
        </LabelWrapper>
    );
}
